/* globals window, CSS, requestAnimationFrame */

import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints, forms } from '@birdi/theme';
import { organisation as organisationApi, stroot } from '@birdi/js-sdk';
import { useMetaSelector } from '../../meta/reducer';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ChevronLeftSVG from '@birdi/icons/chevron-left.svg';
import ChevronRightSVG from '@birdi/icons/chevron-right.svg';
import Select from 'react-select';
import { addDays, addWeeks, addMonths, addYears, subDays, subWeeks, subMonths, subYears, format } from 'date-fns';
import { posthog } from '../../lib/posthog';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';

const durationOptions = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const emptyData = (duration: string): any[] => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months.map(month => ({
    timing: month,
    processingGP: 0,
  }));
};

// Custom Tooltip Component
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    return (
      <div css={css`
        background: ${theme.mono0};
        border: 1px solid ${theme.mono20};
        border-radius: 8px;
        padding: 12px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      `}>
        <p css={css`margin: 0 0 8px 0; font-weight: bold; color: ${theme.midnight};`}>
          {label}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} css={css`
            margin: 0;
            color: ${entry.color};
            font-size: 12px;
          `}>
            Processing: {entry.value} GP
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const dateRangeButtonStyles = (theme: BirdiTheme, isDisabled: boolean) => css`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.midnight};
  padding: 0.25rem;
  border-radius: 4px;
  &:hover {
    background-color: ${theme.sand};
  };
  ${isDisabled && css`
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      background-color: transparent;
    }
  `}
`;

export const ProcessingUsage: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme() as BirdiTheme;
  const meta = useMetaSelector();

  const [duration, setDuration] = useState<string>('monthly');
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartDataLoading, setChartDataLoading] = useState(true);
  const [currentDateRange, setCurrentDateRange] = useState('');
  const [currentEndDate, setCurrentEndDate] = useState(new Date());

  const [isDurationDisabled, setIsDurationDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);
  const [isPrevDisabled, setIsPrevDisabled] = useState(false);

  const [loadedOnce, setLoadedOnce] = useState(false);

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  const updateDateRangeAndGetData = (duration: string, direction?: 'prev' | 'next') => {
    let startDate: Date;
    let endDate: Date;

    if (direction) {
      // Handle navigation - move both start and end dates
      if (duration === 'daily') {
        endDate = direction === 'next'
          ? addDays(currentEndDate, 1)
          : subDays(currentEndDate, 1);
        startDate = endDate; // Same day for daily
      } else if (duration === 'weekly') {
        endDate = direction === 'next'
          ? addWeeks(currentEndDate, 1)
          : subWeeks(currentEndDate, 1);
        startDate = subDays(endDate, 6); // 7 days ending at endDate
      } else if (duration === 'monthly') {
        if (direction === 'next') {
          endDate = addMonths(currentEndDate, 1);
        } else {
          endDate = subMonths(currentEndDate, 1);
        }
        startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1); // 1st day of target month
        endDate = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0); // Last day of target month
      } else if (duration === 'yearly') {
        if (direction === 'next') {
          endDate = addYears(currentEndDate, 1);
        } else {
          endDate = subYears(currentEndDate, 1);
        }
        startDate = new Date(endDate.getFullYear(), 0, 1); // January 1st of the target year
        endDate = new Date(endDate.getFullYear(), 11, 31); // December 31st of the target year
      } else {
        endDate = new Date();
        startDate = new Date();
      }
    } else {
      // Initial load or duration change - show complete periods that include today
      const today = new Date();
      if (duration === 'daily') {
        startDate = today; // Today
        endDate = today;
      } else if (duration === 'weekly') {
        // Find the start of the week (Monday) that includes today
        const dayOfWeek = today.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday = 0, so convert to Monday = 0
        startDate = subDays(today, daysFromMonday);
        endDate = addDays(startDate, 6); // Sunday
      } else if (duration === 'monthly') {
        // Find the first and last days of the current month
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of current month
      } else if (duration === 'yearly') {
        // Find the start of the year that includes today
        startDate = new Date(today.getFullYear(), 0, 1); // January 1st
        endDate = new Date(today.getFullYear(), 11, 31); // December 31st
      } else {
        startDate = today;
        endDate = today;
      }
    }
    setCurrentEndDate(endDate); // Store current end date

    // Calculate and format the date range
    if (duration === 'daily') {
      setCurrentDateRange(format(startDate, 'do MMMM yyyy'));
    } else if (duration === 'weekly') {
      setCurrentDateRange(`${format(startDate, 'do MMMM')} - ${format(endDate, 'do MMMM')}`);
    } else if (duration === 'monthly') {
      setCurrentDateRange(format(startDate, 'MMMM yyyy'));
    } else if (duration === 'yearly') {
      setCurrentDateRange(format(startDate, 'yyyy'));
    }

    // Check if the current date range includes today
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    const isRangeIncludesToday = startDate <= todayEnd && endDate >= todayStart;
    setIsNextDisabled(isRangeIncludesToday);

    getChartData(duration, startDate, endDate);
  };

  const getChartData = async (duration: string, startDate: Date, endDate: Date) => {
    setChartDataLoading(true);
    try {
      const res = await organisationApi.getProcessingUsageTrends(
        stroot('gaed6a'),
        duration,
        startDate,
        endDate
      );
      const timingDataResponse = Array.isArray(res.body) ? res.body : [];

      // Transform data with timing labels
      const transformedData = transformTimingLabels(
        timingDataResponse,
        duration
      );

      setChartData(transformedData);

      // Enable dropdown once we've loaded data successfully (even if all zeros)
      if (Array.isArray(timingDataResponse) && timingDataResponse.length > 0) {
        setLoadedOnce(true);
        setIsDurationDisabled(false);
      }
    } catch (e) {
      setChartData(emptyData(duration));
    }
    setChartDataLoading(false);
  }

  useEffect(() => {
    updateDateRangeAndGetData(duration);
  }, [workspaceId]);

  const handleDurationChange = (option: any) => {
    setDuration(option.value);
    setCurrentEndDate(new Date()); // Reset to current date when duration changes
    updateDateRangeAndGetData(option.value);
    posthog?.capture('dashboard processing usage chart duration clicked', { duration: option.value });
  };

  const handleDateRangeChange = (direction: 'prev' | 'next') => {
    updateDateRangeAndGetData(duration, direction);
  };

  const transformTimingLabels = (data: any[], duration: string) => {
    if (!data || data.length === 0) {
      return data;
    }

    const labels = {
      daily: ['12:00am', '1:00am', '2:00am', '3:00am', '4:00am', '5:00am', '6:00am', '7:00am', '8:00am', '9:00am', '10:00am', '11:00am', '12:00pm', '1:00pm', '2:00pm', '3:00pm', '4:00pm', '5:00pm', '6:00pm', '7:00pm', '8:00pm', '9:00pm', '10:00pm', '11:00pm'],
      weekly: ['Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat', 'Sun'],
      monthly: ['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'],
      yearly: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    };

    const durationLabels = labels[duration as keyof typeof labels] || [];

    return data.map((item, index) => ({
      ...item,
      timing: durationLabels[index] || item.timing
    }));
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
    >
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
        id={suppressIntercomAnchor ? undefined : 'processing-usage'}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Processing usage</span>
      </div>
      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        `}
      >
        <div css={css`
            margin-bottom: 1em;
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <span>Processing activity over time</span>
          <Select
            isDisabled={isDurationDisabled}
            options={durationOptions}
            value={{ value: duration, label: duration.charAt(0).toUpperCase() + duration.slice(1) }}
            onChange={handleDurationChange}
            styles={{
              ...forms.reactSelect,
              valueContainer: (provided: any) => ({
                ...provided,
                padding: '5px',
                minWidth: '80px',
              }),
              singleValue: (provided: any) => ({
                ...provided,
                color: theme.midnight,
              }),
              menu: (provided: any) => ({
                ...provided,
                zIndex: 11,
              }),
              control: (provided: any, state: any) => ({
                ...provided,
                borderColor: theme.mono40,
                boxShadow: 'none',
                '&:hover': {
                  borderColor: theme.midnight,
                  boxShadow: `0 0 1px 1px ${theme.mono30}`,
                },
                '&:focus': {
                  borderColor: theme.mono40,
                },
              }),
            } as any}
            isSearchable={false}
          />
        </div>

        {/* Chart Area */}
        <div
          css={css`
            min-height: 210px;
            position: relative;
            background: ${theme.mono10};
            border: 1px solid ${theme.mono10};
            border-radius: 8px;
            display: flex;
            flex-direction: column;
          `}
        >
          {/* Date Navigation */}
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              margin: 1em 0;
            `}
          >
            <button
              css={dateRangeButtonStyles(theme, isDurationDisabled || isPrevDisabled)}
              onClick={() => handleDateRangeChange('prev')}
              disabled={isDurationDisabled || isPrevDisabled}
            >
              <ChevronLeftSVG />
            </button>
            <span css={css`font-weight: 500; margin: 0 1rem;`}>{currentDateRange}</span>
            <button
              onClick={() => handleDateRangeChange('next')}
              disabled={isDurationDisabled || isNextDisabled}
              css={dateRangeButtonStyles(theme, isDurationDisabled || isNextDisabled)}
            >
              <ChevronRightSVG />
            </button>
          </div>

          {!chartDataLoading && (
            <div css={css`
                width: 100%;
                height: 300px;
                position: relative;
              `}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 10, right: 40, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="2 2" stroke={theme.mono30} vertical={false} />
                  <XAxis dataKey="timing" stroke={theme.mono40} fontSize={13} />
                  <YAxis stroke={theme.mono40} fontSize={13} label={{ value: 'Gigapixels (gp)', angle: -90, position: 'insideLeft' }} />
                  <Tooltip
                    content={<CustomTooltip />}
                  />
                  {chartData.some(item => (item.processingGP || 0) > 0) && (
                    <Bar
                      dataKey="processingGP"
                      fill={theme.flora}
                      radius={[4, 4, 0, 0]}
                    />
                  )}
                </BarChart>
              </ResponsiveContainer>

              {/* No data message overlay */}
              {!loadedOnce && !chartData.some(item => (item.processingGP || 0) > 0) && (
                <div css={css`
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  text-align: center;
                  z-index: 10;
                  padding: 1em;
                  color: ${theme.mono60};
                `}>
                  <p css={css`margin: 0 0 0.5em 0; font-weight: 600;`}>
                    Your processing usage will appear here.
                  </p>
                  <p css={css`margin: 0; font-size: 0.9em;`}>
                    Run processing on your latest imagery or map to start seeing results in this space.
                  </p>
                </div>
              )}
            </div>
          )}

          {chartDataLoading && (
            <div css={css`
                height: 100%;
                min-height: 300px;
                display: flex;
                align-items: center;
                justify-content: center;
                color: ${theme.midnight};
              `}
            >
              Loading...
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

